import { normalizeChar } from './validation.js';
/**
 * Valid color codes
 */
export const COLOR_CODES = ['W', 'R', 'G', 'B', 'Y', 'O', 'P', 'C', 'K', 'L'];
/**
 * Color hex values for rendering
 */
export const COLOR_MAP = {
    W: '#f5f5f0', // White (default)
    R: '#ef4444', // Red
    G: '#22c55e', // Green
    B: '#3b82f6', // Blue
    Y: '#eab308', // Yellow
    O: '#f97316', // Orange
    P: '#a855f7', // Purple
    C: '#06b6d4', // Cyan
    K: '#ec4899', // Pink
    L: '#1a1a1a', // Black
};
/**
 * Format time according to clock format string
 * Supports: HH (24hr), hh (12hr), mm (min), ss (sec), A/a (AM/PM)
 * Case-insensitive: automatically detects 12-hour format if A/a is present
 */
export function formatClockTime(format, date = new Date()) {
    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const ampm = hours24 < 12 ? 'am' : 'pm';
    // Detect if this is a 12-hour format (contains A or a for AM/PM)
    const is12Hour = /[Aa]/.test(format);
    let result = format;
    // Replace hours - use 12-hour if AM/PM is present, otherwise 24-hour
    result = result.replace(/HH/gi, () => {
        const h = is12Hour ? hours12 : hours24;
        return h.toString().padStart(2, '0');
    });
    // Minutes - case insensitive (MM or mm both mean minutes)
    result = result.replace(/MM/gi, minutes.toString().padStart(2, '0'));
    // Seconds - case insensitive (SS or ss both mean seconds)
    result = result.replace(/SS/gi, seconds.toString().padStart(2, '0'));
    // AM/PM - preserve the case of the token
    result = result.replace(/A/g, ampm.toUpperCase());
    result = result.replace(/a/g, ampm);
    return result;
}
/**
 * Get character count for a clock format (for word-wrapping calculations)
 */
export function getClockLength(format) {
    return formatClockTime(format).length;
}
/**
 * Parse text with color markup like {R}red text{W} and background [R]
 * Also handles clock markup like <clock:HH:mm>
 * Returns array of {char, color, bg, clock} objects
 */
export function parseColorMarkup(text) {
    const result = [];
    let currentColor = 'W';
    let currentBg = undefined;
    let i = 0;
    while (i < text.length) {
        // Check for text color code pattern {X}
        if (text[i] === '{' && i + 2 < text.length && text[i + 2] === '}') {
            const code = text[i + 1].toUpperCase();
            if (COLOR_CODES.includes(code)) {
                currentColor = code;
                i += 3; // Skip {X}
                continue;
            }
        }
        // Check for background color code pattern [X] or [-] to clear
        if (text[i] === '[' && i + 2 < text.length && text[i + 2] === ']') {
            const code = text[i + 1].toUpperCase();
            if (code === '-') {
                currentBg = undefined; // Clear background
                i += 3;
                continue;
            }
            if (COLOR_CODES.includes(code)) {
                currentBg = code;
                i += 3; // Skip [X]
                continue;
            }
        }
        // Check for clock pattern <clock:FORMAT> (case-insensitive)
        if (text[i] === '<' && text.slice(i, i + 7).toLowerCase() === '<clock:') {
            const endIdx = text.indexOf('>', i);
            if (endIdx !== -1) {
                const format = text.slice(i + 7, endIdx);
                const formatted = formatClockTime(format);
                // Add each character of the formatted time with clock metadata
                for (let j = 0; j < formatted.length; j++) {
                    result.push({
                        char: formatted[j],
                        color: currentColor,
                        bg: currentBg,
                        clock: { format, index: j }
                    });
                }
                i = endIdx + 1;
                continue;
            }
        }
        // Regular character
        result.push({ char: text[i], color: currentColor, bg: currentBg });
        i++;
    }
    return result;
}
/**
 * Strip color codes from text (for length calculations)
 * Strips both text color {X} and background color [X] codes
 */
export function stripColorCodes(text) {
    return text.replace(/\{[WRGBYOPCK]\}/gi, '').replace(/\[[WRGBYOPCK-]\]/gi, '');
}
/**
 * Strip clock markup and replace with equivalent-length placeholder
 * Needed for accurate length calculations in word-wrapping
 */
export function stripClockMarkup(text) {
    return text.replace(/<clock:([^>]+)>/gi, (_match, format) => {
        return '_'.repeat(getClockLength(format));
    });
}
/**
 * Strip all markup (colors and clocks) for length calculations
 */
export function stripAllMarkup(text) {
    let result = stripColorCodes(text);
    result = stripClockMarkup(result);
    return result;
}
/**
 * Wraps text to fit within board dimensions
 * Handles newlines and pads/truncates as needed
 * Supports color, background, and clock markup
 */
export function wrapText(text, cols, rows) {
    const lines = text.split('\n');
    const result = [];
    for (let row = 0; row < rows; row++) {
        const rowCells = [];
        const line = row < lines.length ? lines[row] : '';
        const parsed = parseColorMarkup(line);
        for (let col = 0; col < cols; col++) {
            if (col < parsed.length) {
                const { char, color, bg, clock } = parsed[col];
                const normalizedChar = normalizeChar(char);
                // Only include color/bg/clock if not default
                if (color === 'W' && !bg && !clock) {
                    rowCells.push(normalizedChar);
                }
                else if (!bg && !clock) {
                    rowCells.push({ char: normalizedChar, color });
                }
                else if (color === 'W' && !clock) {
                    rowCells.push({ char: normalizedChar, bg });
                }
                else if (color === 'W' && !bg) {
                    rowCells.push({ char: normalizedChar, clock });
                }
                else {
                    // Include all non-default fields
                    const cell = { char: normalizedChar };
                    if (color !== 'W')
                        cell.color = color;
                    if (bg)
                        cell.bg = bg;
                    if (clock)
                        cell.clock = clock;
                    rowCells.push(cell);
                }
            }
            else {
                rowCells.push(' ');
            }
        }
        result.push(rowCells);
    }
    return result;
}
/**
 * Auto-wrap message across rows (for single-line input)
 * Matches existing setMessage behavior from split-flap.js
 * Supports color and clock markup
 */
export function autoWrapMessage(text, cols, rows) {
    // Strip all markup for whitespace/formatting checks
    const stripped = stripAllMarkup(text);
    // If has newlines, leading/trailing whitespace, or multiple consecutive spaces, preserve formatting
    // Also use wrapText if the text has no spaces (single word with markup) to avoid word-wrap truncation issues
    if (stripped.includes('\n') || /^\s/.test(stripped) || /\s$/.test(stripped) || /\s{2,}/.test(stripped) || !stripped.includes(' ')) {
        return wrapText(text, cols, rows);
    }
    // For word wrapping, we need to work with stripped text for length calculations
    // but preserve color codes in the final output
    const words = stripped.split(' ');
    const lines = [];
    let currentLine = '';
    const originalLines = [];
    let currentOriginalLine = '';
    // Split original text by spaces (keeping color codes and clock tokens)
    const originalWords = [];
    let wordStart = 0;
    for (let i = 0; i < text.length; i++) {
        // Skip color codes
        if (text[i] === '{' && i + 2 < text.length && text[i + 2] === '}') {
            const code = text[i + 1].toUpperCase();
            if (COLOR_CODES.includes(code)) {
                i += 2; // Skip to end of color code (loop will add 1)
                continue;
            }
        }
        // Skip background color codes
        if (text[i] === '[' && i + 2 < text.length && text[i + 2] === ']') {
            i += 2;
            continue;
        }
        // Skip clock tokens (case-insensitive)
        if (text[i] === '<' && text.slice(i, i + 7).toLowerCase() === '<clock:') {
            const endIdx = text.indexOf('>', i);
            if (endIdx !== -1) {
                i = endIdx; // Skip to end of clock (loop will add 1)
                continue;
            }
        }
        if (text[i] === ' ') {
            if (i > wordStart) {
                originalWords.push(text.substring(wordStart, i));
            }
            wordStart = i + 1;
        }
    }
    if (wordStart < text.length) {
        originalWords.push(text.substring(wordStart));
    }
    // Word wrap using stripped lengths but keeping original words
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const originalWord = originalWords[i] || word;
        const separator = currentLine ? ' ' : '';
        const originalSeparator = currentOriginalLine ? ' ' : '';
        if (currentLine.length + separator.length + word.length <= cols) {
            currentLine += separator + word;
            currentOriginalLine += originalSeparator + originalWord;
        }
        else {
            if (currentLine) {
                lines.push(currentLine);
                originalLines.push(currentOriginalLine);
            }
            currentLine = word.length > cols ? word.substring(0, cols) : word;
            // For original, we need to handle truncation with color codes and clock tokens
            if (stripAllMarkup(originalWord).length > cols) {
                // Truncate carefully preserving markup up to cols chars
                let truncated = '';
                let charCount = 0;
                for (let j = 0; j < originalWord.length && charCount < cols; j++) {
                    // Skip color codes
                    if (originalWord[j] === '{' && j + 2 < originalWord.length && originalWord[j + 2] === '}') {
                        const code = originalWord[j + 1].toUpperCase();
                        if (COLOR_CODES.includes(code)) {
                            truncated += originalWord.substring(j, j + 3);
                            j += 2;
                            continue;
                        }
                    }
                    // Skip background codes
                    if (originalWord[j] === '[' && j + 2 < originalWord.length && originalWord[j + 2] === ']') {
                        truncated += originalWord.substring(j, j + 3);
                        j += 2;
                        continue;
                    }
                    // Handle clock tokens (treat as atomic - include whole thing or nothing)
                    if (originalWord[j] === '<' && originalWord.slice(j, j + 7).toLowerCase() === '<clock:') {
                        const endIdx = originalWord.indexOf('>', j);
                        if (endIdx !== -1) {
                            const clockToken = originalWord.substring(j, endIdx + 1);
                            const format = originalWord.slice(j + 7, endIdx);
                            const clockLen = getClockLength(format);
                            // Only include clock if it fits within remaining space
                            if (charCount + clockLen <= cols) {
                                truncated += clockToken;
                                charCount += clockLen;
                                j = endIdx;
                            }
                            continue;
                        }
                    }
                    truncated += originalWord[j];
                    charCount++;
                }
                currentOriginalLine = truncated;
            }
            else {
                currentOriginalLine = originalWord;
            }
        }
    }
    if (currentLine) {
        lines.push(currentLine);
        originalLines.push(currentOriginalLine);
    }
    return wrapText(originalLines.slice(0, rows).join('\n'), cols, rows);
}
/**
 * Create empty cells grid
 */
export function createEmptyCells(rows, cols) {
    return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ' '));
}
/**
 * Helper to get char from CellData
 */
export function getCellChar(cell) {
    return typeof cell === 'string' ? cell : cell.char;
}
/**
 * Helper to get color from CellData
 */
export function getCellColor(cell) {
    return typeof cell === 'string' ? undefined : cell.color;
}
/**
 * Helper to get background color from CellData
 */
export function getCellBg(cell) {
    return typeof cell === 'string' ? undefined : cell.bg;
}
//# sourceMappingURL=text-utils.js.map